import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { formatPhoneNumber } from '@stitch/pbx-adapter/catalog';
import { api, type AdminNumbersView, type E911Address, type InventoryRow, type NumberProblem } from '../../api/client';
import { ORDER_STATUS, OrderLines, oneLine } from '../../components/numbers';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { dateOnly, plural, shortWhen } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';

const PROBLEMS: Record<NumberProblem, { label: string; detail: string }> = {
  no_e911: { label: 'No 911 address', detail: 'Calls to 911 from it send no address.' },
  no_tenant: { label: 'Not on a company', detail: 'Bought, but on no tenant: billed and unused.' },
  call_flow: { label: 'Other routing', detail: 'The carrier sends its calls somewhere other than Stitch’s routing.' },
  porting: { label: 'Moving in', detail: 'Still being moved from another provider.' },
  not_at_carrier: { label: 'Not at the carrier', detail: 'On a tenant, but not in the carrier’s inventory.' }
};
type Filter = 'all' | NumberProblem;

/** Every number at the carrier and on the phone system, what needs fixing, and every order. */
export function AdminNumbers() {
  const [fresh, setFresh] = useState(0);
  const data = useData(() => api.adminNumbers(fresh > 0), [fresh]);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<InventoryRow | null>(null);
  const [releasing, setReleasing] = useState<InventoryRow | null>(null);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const v = data.data;
  const flows = new Map((v?.callFlows ?? []).map((f) => [f.id, f.name]));
  // With more than one carrier account (1.12.25), each number says which it's on.
  const accountsShown = new Set((v?.inventory ?? []).map((r) => r.carrier?.account?.id).filter(Boolean)).size > 1;
  const counts = useMemo(() => {
    const c = new Map<Filter, number>([['all', v?.inventory.length ?? 0]]);
    for (const r of v?.inventory ?? []) for (const p of r.problems) c.set(p, (c.get(p) ?? 0) + 1);
    return c;
  }, [v]);
  const rows = useMemo(() => {
    const needle = q.replace(/\D/g, '') || q.trim().toLowerCase();
    return (v?.inventory ?? []).filter((r) => (filter === 'all' || r.problems.includes(filter)) && (!needle || r.number.includes(needle) || (r.tenant ?? '').includes(needle)));
  }, [v, filter, q]);
  const open = (v?.orders ?? []).filter((o) => ['working', 'needs_staff', 'porting', 'partial', 'failed'].includes(o.status));

  async function act(fn: () => Promise<unknown>, done: string) {
    setError('');
    try {
      await fn();
      setToast(done);
      data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={v ? `${plural(v.inventory.length, 'number')} at the carrier` : 'Stitch Admin'}
        title="Numbers"
        actions={
          <>
            <label className="search">
              <Icon name="search" size={17} strokeWidth={2} />
              <span className="sr-only">Search numbers</span>
              <input type="search" placeholder="Number or tenant" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Button icon="refresh" onClick={() => setFresh((f) => f + 1)} disabled={data.loading}>
              Refresh from the carrier
            </Button>
          </>
        }
      />
      <div className="content">
        {error && <Notice tone="crit">{error}</Notice>}
        {data.error ? (
          <Card>
            <LoadError error={data.error} retry={data.reload} />
          </Card>
        ) : !v ? (
          <Loading label="Loading numbers from the carrier" />
        ) : (
          <>
            <Setup v={v} flows={flows} />
            {v.inventoryError && <Notice tone="crit" title="Couldn’t read the carrier’s inventory">{v.inventoryError}</Notice>}

            {open.length > 0 && (
              <Card title={`Needs a look · ${open.length}`}>
                <ul className="order-list">
                  {open.map((o) => (
                    <li key={o.id}>
                      <div className="order-row static">
                        <span className="cell-stack grow">
                          <strong>
                            {o.company} · {o.kind === 'port' ? `moving ${plural(o.lines.length, 'number')} in` : plural(o.lines.length, 'new number')}
                          </strong>
                          <small className="muted">
                            {o.requestedByName}, {shortWhen(o.createdAt)} · <span className="mono">{o.id}</span>
                            {o.carrierRef ? <> · carrier <span className="mono">{o.carrierRef}</span></> : null}
                            {o.port?.focDate ? ` · moving on ${o.port.focDate}` : o.port ? ` · asked for ${o.port.wantedDate}${o.port.carrierStatus ? `, ${o.port.carrierStatus}` : ''}` : ''}
                          </small>
                        </span>
                        <Chip small tone={ORDER_STATUS[o.status].tone}>
                          {ORDER_STATUS[o.status].label}
                        </Chip>
                        {(o.status === 'partial' || o.status === 'needs_staff') && (
                          <Button size="small" icon="refresh" onClick={() => void act(() => api.retryOrder(o.id), 'Tried the unfinished steps again.')}>
                            Try again
                          </Button>
                        )}
                      </div>
                      <div className="order-detail">
                        <OrderLines order={o} staff />
                        {o.lines.some((l) => l.bought && l.pbx === 'waiting') && (
                          <div className="connect-list">
                            <span className="muted small-print">Add these on the phone system (tenant {o.tenant}, sent to {o.destination || 'its number inventory, 66001'}), then mark them connected:</span>
                            {o.lines
                              .filter((l) => l.bought && l.pbx === 'waiting')
                              .map((l) => (
                                <Button key={l.number} size="small" icon="check" onClick={() => void act(() => api.markConnected(o.id, l.number), `${formatPhoneNumber(l.number)} is connected.`)}>
                                  {formatPhoneNumber(l.number)} connected
                                </Button>
                              ))}
                          </div>
                        )}
                        {o.error && <small className="muted">{o.error}</small>}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <div className="filters" role="group" aria-label="Show numbers">
              {(['all', 'no_e911', 'no_tenant', 'call_flow', 'porting', 'not_at_carrier'] as Filter[])
                .filter((f) => f === 'all' || (counts.get(f) ?? 0) > 0)
                .map((f) => (
                  <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                    {f === 'all' ? 'All' : PROBLEMS[f].label} <span className="filter-count">{counts.get(f) ?? 0}</span>
                  </button>
                ))}
            </div>
            <Card>
              {rows.length === 0 ? (
                <Empty title={v.inventory.length ? 'Nothing matches' : 'No numbers at the carrier'}>{v.inventory.length ? 'Try another filter.' : v.carrier ? 'Numbers bought here show up once they’re in the carrier’s inventory.' : 'Connect the carrier on System → Carrier accounts.'}</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">Number</th>
                        <th scope="col">Company</th>
                        <th scope="col">Carrier routing</th>
                        <th scope="col">Needs</th>
                        <th scope="col">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.number}>
                          <td>
                            <button type="button" className="link-btn mono" onClick={() => setDetail(r)}>
                              {formatPhoneNumber(r.number)}
                            </button>
                          </td>
                          <td>
                            {r.tenant ? (
                              <span className="cell-stack">
                                <Link to={`/t/${encodeURIComponent(r.tenant)}/numbers`}>{r.tenant}</Link>
                                <small className="muted">{r.destination ? `to ${r.destination}` : 'not going anywhere'}</small>
                              </span>
                            ) : (
                              <span className="muted">None</span>
                            )}
                          </td>
                          <td>{r.carrier ? <span className="cell-stack"><span>{flows.get(r.carrier.callFlow) ?? r.carrier.callFlow ?? '—'}</span><small className="muted">{r.carrier.rateCenter}{r.carrier.state ? `, ${r.carrier.state}` : ''}{r.carrier.account && accountsShown ? ` · ${r.carrier.account.name}` : ''}</small></span> : '—'}</td>
                          <td>
                            {r.problems.length === 0 ? (
                              <Chip small tone="ok">
                                Fine
                              </Chip>
                            ) : (
                              <span className="chip-row">
                                {r.problems.map((p) => (
                                  <Chip key={p} small tone={p === 'no_e911' || p === 'no_tenant' ? 'warn' : 'neutral'}>
                                    {PROBLEMS[p].label}
                                  </Chip>
                                ))}
                              </span>
                            )}
                          </td>
                          <td className="cell-action">
                            <span className="row-actions">
                              {r.problems.includes('call_flow') && v.carrier?.writes && v.callFlow && (
                                <Button size="small" onClick={() => void act(() => api.fixCallFlow(r.number), `${formatPhoneNumber(r.number)} now routes to Stitch.`)}>
                                  Route to Stitch
                                </Button>
                              )}
                              {r.carrier && v.carrier?.writes && r.heldUntil && (
                                <span className="muted small-print" title="The carrier keeps new numbers for at least 90 days.">
                                  Kept until {dateOnly(r.heldUntil)}
                                </span>
                              )}
                              {r.carrier && v.carrier?.writes && !r.heldUntil && (
                                <Button size="small" variant="ghost" className="danger-text" onClick={() => setReleasing(r)}>
                                  Release
                                </Button>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card title="All orders and moves">
              {v.orders.length === 0 ? (
                <Empty title="None yet">Numbers customers get or move in show up here.</Empty>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">When</th>
                        <th scope="col">Company</th>
                        <th scope="col">What</th>
                        <th scope="col">Status</th>
                        <th scope="col">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.orders.map((o) => (
                        <tr key={o.id}>
                          <td>{shortWhen(o.createdAt)}</td>
                          <td>{o.company}</td>
                          <td>
                            <span className="cell-stack">
                              <span>{o.kind === 'port' ? `Move in: ${plural(o.lines.length, 'number')}` : plural(o.lines.length, 'new number')}</span>
                              <small className="muted mono">{o.lines.map((l) => formatPhoneNumber(l.number)).join(', ')}</small>
                            </span>
                          </td>
                          <td>
                            <Chip small tone={ORDER_STATUS[o.status].tone}>
                              {ORDER_STATUS[o.status].label}
                            </Chip>
                          </td>
                          <td>{o.requestedByName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <details className="card endpoints-card">
              <summary className="card-head">
                <h2>Carrier API calls · {v.endpoints.filter((e) => e.verified).length} of {v.endpoints.length} confirmed</h2>
              </summary>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Call</th>
                      <th scope="col">Path</th>
                      <th scope="col">Kind</th>
                      <th scope="col">Confirmed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.endpoints.map((e) => (
                      <tr key={e.name}>
                        <td>{e.name}</td>
                        <td className="mono">{e.path}</td>
                        <td>{e.write ? <Chip small tone="warn">Changes things</Chip> : 'Read'}</td>
                        <td>{e.verified ? <Chip small tone="ok">Yes</Chip> : <span className="muted">From the docs</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )}
      </div>

      {detail && <NumberDetail row={detail} flows={flows} onClose={() => setDetail(null)} />}
      {releasing && (
        <Release
          row={releasing}
          onClose={() => setReleasing(null)}
          onDone={(message) => {
            setReleasing(null);
            setToast(message);
            data.reload();
          }}
        />
      )}
      {toast && <Toast tone="ok" message={toast} onDismiss={() => setToast('')} />}
    </>
  );
}

/** Whether buying, 911 and moves are ready, and what's missing. */
function Setup({ v, flows }: { v: AdminNumbersView; flows: Map<string, string> }) {
  const items: { ok: boolean; text: string; fix?: string }[] = [
    { ok: Boolean(v.carrier), text: v.carrier ? `Carrier connected: ${v.carrier.name}` : 'No carrier connected', fix: 'Add the carrier’s API token on System → Carrier accounts.' },
    {
      ok: Boolean(v.callFlow),
      text: v.callFlow ? `New numbers route to ${flows.get(v.callFlow) ?? 'call flow'} (${v.callFlow})` : 'No routing chosen for new numbers',
      fix: 'Choose the call flow that sends calls to the phone system on System → Carrier (the list below shows them).'
    },
    {
      ok: Boolean(v.trunk),
      text: v.trunk ? `New numbers join the phone system on trunk ${v.trunk}` : 'No trunk set for new numbers on the phone system',
      fix: 'Choose the trunk Ahoi numbers come in on, on System → Carrier. Until then Stitch uses the trunk most of each company’s numbers use, and a company with none waits for you.'
    },
    { ok: Boolean(v.carrier?.writes), text: v.carrier?.writes ? 'Buying, releasing, 911 changes and moves are on' : 'Buying, 911 changes and moves are off (read-only)', fix: 'Turn Spending on, on System → Carrier, when ready: these are real and cost money.' }
  ];
  return (
    <Card title="Setup">
      <ul className="setup-list card-body">
        {items.map((i) => (
          <li key={i.text} className={i.ok ? 'ok' : 'todo'}>
            <Icon name={i.ok ? 'check' : 'alert'} size={16} strokeWidth={2.3} />
            <span className="cell-stack">
              <span>{i.text}</span>
              {!i.ok && i.fix && <small className="muted">{i.fix}</small>}
            </span>
          </li>
        ))}
      </ul>
      {v.callFlows.length > 0 && (
        <div className="card-body">
          <span className="field-label">Call flows at the carrier</span>
          <ul className="plain-list">
            {v.callFlows.map((f) => (
              <li key={f.id}>
                <span className="mono">{f.id}</span> {f.name}
                {f.routes.length > 0 && <span className="muted mono"> → {f.routes.join(', ')}</span>}
                {f.id === v.callFlow && <Chip small tone="ok">New numbers</Chip>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

/** One number: what the carrier has, and its 911 address (looked up when opened). */
function NumberDetail({ row, flows, onClose }: { row: InventoryRow; flows: Map<string, string>; onClose: () => void }) {
  const e911 = useData<{ address: E911Address | null } | null>(() => (row.carrier ? api.adminE911(row.number) : Promise.resolve(null)), [row.number]);
  return (
    <Dialog open onClose={onClose} title={formatPhoneNumber(row.number)} subtitle={row.number}>
      <dl className="dl">
        <dt>Company</dt>
        <dd>{row.tenant ?? 'None'}</dd>
        <dt>Calls go to</dt>
        <dd>{row.destination || '—'}</dd>
        <dt>Carrier routing</dt>
        <dd>{row.carrier ? `${flows.get(row.carrier.callFlow) ?? ''} (${row.carrier.callFlow || 'none'})` : 'Not at the carrier'}</dd>
        <dt>Rate center</dt>
        <dd>{row.carrier ? `${row.carrier.rateCenter}${row.carrier.state ? `, ${row.carrier.state}` : ''}` : '—'}</dd>
        {row.carrier?.account && (
          <>
            <dt>Carrier account</dt>
            <dd>{row.carrier.account.name}</dd>
          </>
        )}
        {row.heldUntil && (
          <>
            <dt>Minimum term</dt>
            <dd>Bought less than 90 days ago: it can be released from {dateOnly(row.heldUntil)}.</dd>
          </>
        )}
        <dt>Notes at the carrier</dt>
        <dd>{row.carrier?.notes || '—'}</dd>
        <dt>911 address</dt>
        <dd>{!row.carrier ? '—' : e911.error ? <span className="error">{e911.error.message}</span> : !e911.data ? 'Looking it up…' : e911.data.address ? oneLine(e911.data.address) : <Chip small tone="warn">None</Chip>}</dd>
        {row.problems.length > 0 && (
          <>
            <dt>Needs</dt>
            <dd>
              <ul className="plain-list">
                {row.problems.map((p) => (
                  <li key={p}>
                    <strong>{PROBLEMS[p].label}.</strong> <span className="muted">{PROBLEMS[p].detail}</span>
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
      </dl>
    </Dialog>
  );
}

/** Giving a number back to the carrier: typed back first, since it can't be undone. */
function Release({ row, onClose, onDone }: { row: InventoryRow; onClose: () => void; onDone: (message: string) => void }) {
  const pretty = formatPhoneNumber(row.number);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const matches = typed.replace(/\D/g, '').slice(-10) === row.number.replace(/\D/g, '').slice(-10);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Release ${pretty}?`}
      foot={
        <>
          <Button
            variant="danger"
            disabled={!matches || busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                const r = await api.releaseNumber(row.number, typed);
                onDone(`${pretty} is released${r.removedFromTenant ? ` and off ${r.removedFromTenant}` : ''}.`);
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
                setBusy(false);
              }
            }}
          >
            {busy ? 'Releasing…' : 'Release it'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
        </>
      }
    >
      <Notice tone="warn">
        {row.tenant ? `It comes off ${row.tenant} first, so calls to it stop. ` : ''}The carrier takes it back and it may go to someone else: this can’t be undone.
      </Notice>
      <Field label={`Type ${pretty} to confirm`} value={typed} onChange={(e) => setTyped(e.target.value)} inputMode="tel" autoFocus autoComplete="off" />
      {error && <Notice tone="crit">{error}</Notice>}
    </Dialog>
  );
}
